/* eslint-disable no-param-reassign */
// (function (global) {
'use strict';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';

const util = newUtil();
const inliner = newInliner();
const fontFaces = newFontFaces();
const images = newImages();

// Default impl options
const defaultOptions = {
    // Default is to fail on error, no placeholder
    imagePlaceholder: 'images/default.png',
    // Default cache bust is false, it will use the cache
    cacheBust: true,
};

const domtoimage = {
    toSvg,
    toPng,
    toCanvas,
    toJpeg,
    toBlob,
    toPixelData,
    impl: {
        fontFaces,
        images,
        util,
        inliner,
        options: {} as any,
    },
};

function svgToBase64ByCanvas(src) {
    return new Promise((resolve) => {
        const matchRes = src.match(/viewBox='((\d+\s?)+)'/);
        const [,, width, height] = matchRes?.[1].split(' ') || [];
        const canvas = document.createElement('canvas');
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = src;
        img.onload = function () {
            canvas.width = width || img.width;
            canvas.height = height || img.height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0);
            resolve(canvas.toDataURL());
        };
        img.onerror = function () {
            resolve('');
        };
    });
}

// 非递归、深度优先遍历
// function dFSDomTraversal(root) {
//     if (!root) return;
//     const arr = [];
//     const queue = [root];
//     let node = queue.shift();
//     while (node) {
//         arr.push(node);
//         if (node.children.length) {
//             for (let i = node.children.length - 1; i >= 0; i--) {
//                 queue.unshift(node.children[i]);
//             }
//         }
//         node = queue.shift();
//     }

//     return arr;
// }


// function copyStyle(element) {
//     const CSSRules = [
//         'display', 'color', 'font-size', 'line-height', 'width', 'height', 'align-items',
//         'position', 'left', 'top', 'right', 'bottom',
//         'background',
//         'padding',
//         'margin',
//         'transform',
//     ];
//     const styles = getComputedStyle(element);
//     CSSRules.forEach((rule) => {
//         element.style.setProperty(rule, styles.getPropertyValue(rule));
//     });
// }

/**
 * 检查节点名称。
 * 为什么不直接用instatnce Element ？
 * 答：如果在iframe中获取的元素，因为有上下文关系，必须通过当前window.Element来进行对比。
 * 那么使用以上方式就会对比出错，这里直接改成tagName对比
 * @param node dom元素
 * @param tagName 检查匹配的tagName
 * @return {boolean}
 */
function checkTagName(node, tagName?) {
    const nName = node.tagName;
    if (nName && typeof nName === 'string') {
        if (tagName && typeof tagName === 'string') {
            return tagName.toLowerCase() === nName.toLowerCase();  // 忽略大小写
        }
        return true; // 不传tagName，直接对比成功
    }
    return false;
}

// if (typeof module !== 'undefined')
//     module.exports = domtoimage;
// else
//     global.domtoimage = domtoimage;


/**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options
     * @param {Function} options.filter - Should return true if passed node should be included in the output
     *          (excluding node means excluding it's children as well). Not called on the root node.
     * @param {String} options.bgcolor - color for the background, any valid CSS color value.
     * @param {Number} options.width - width to be applied to node before rendering.
     * @param {Number} options.height - height to be applied to node before rendering.
     * @param {Object} options.style - an object whose properties to be copied to node's style before rendering.
     * @param {Number} options.quality - a Number between 0 and 1 indicating image quality (applicable to JPEG only),
                defaults to 1.0.
     * @param {String} options.imagePlaceholder - dataURL to use as a placeholder
     * for failed images, default behaviour is to fail fast on images we can't fetch
     * @param {Boolean} options.cacheBust - set to true to cache bust by appending the time to the request url
     * @return {Promise} - A promise that is fulfilled with a SVG image data URL
     * */
function toSvg(node, options) {
    options = options || {};
    options.resolution = options.resolution || 1;
    options.cloneNode = options.cloneNode ?? false;
    copyOptions(options);
    return Promise.resolve(node)
        .then(node => (options.cloneNode ? cloneNode(node, options.filter, true) : node))
        .then(embedFonts)
        .then(inlineImages)
        .then(applyOptions)
        .then(clone => makeSvgDataUri(
            clone,
            (options.width || util.width(node)) * options.resolution,
            (options.height || util.height(node)) * options.resolution,
        ));

    function applyOptions(clone) {
        if (options.bgcolor) clone.style.backgroundColor = options.bgcolor;

        if (options.width) clone.style.width = `${options.width}px`;
        if (options.height) clone.style.height = `${options.height}px`;

        if (options.style) {
            Object.keys(options.style).forEach((property) => {
                clone.style[property] = options.style[property];
            });
        }

        return clone;
    }
}

/**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options, @see {@link toSvg}
     * @return {Promise} - A promise that is fulfilled with a Uint8Array containing RGBA pixel data.
     * */
function toPixelData(node, options) {
    return draw(node, options || {})
        .then(canvas => canvas.getContext('2d')?.getImageData(
            0,
            0,
            util.width(node),
            util.height(node),
        ).data);
}

/**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options, @see {@link toSvg}
     * @return {Promise} - A promise that is fulfilled with a PNG image data URL
     * */
function toPng(node, options) {
    return toCanvas(node, options)
        .then(canvas => canvas.toDataURL());
}

function toCanvas(node, options) {
    return draw(node, options || {});
}

/**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options, @see {@link toSvg}
     * @return {Promise} - A promise that is fulfilled with a JPEG image data URL
     * */
function toJpeg(node, options) {
    options = options || {};
    return draw(node, options)
        .then(canvas => canvas.toDataURL('image/jpeg', options.quality || 1.0));
}

/**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options, @see {@link toSvg}
     * @return {Promise} - A promise that is fulfilled with a PNG image blob
     * */
function toBlob(node, options) {
    return draw(node, options || {})
        .then(util.canvasToBlob);
}

function copyOptions(options) {
    // Copy options to impl options for use in impl
    if (typeof (options.imagePlaceholder) === 'undefined') {
        domtoimage.impl.options.imagePlaceholder = defaultOptions.imagePlaceholder;
    } else {
        domtoimage.impl.options.imagePlaceholder = options.imagePlaceholder;
    }

    if (typeof (options.cacheBust) === 'undefined') {
        domtoimage.impl.options.cacheBust = defaultOptions.cacheBust;
    } else {
        domtoimage.impl.options.cacheBust = options.cacheBust;
    }
}

function draw(domNode, options) {
    return toSvg(domNode, options)
        .then(util.makeImage)
        .then((image: CanvasImageSource) => {
            const canvas = newCanvas(domNode);
            const ctx = canvas.getContext('2d');
            ctx?.scale(options.resolution, options.resolution);
            ctx?.drawImage(image, 0, 0);
            return canvas;
        });

    function newCanvas(domNode) {
        const canvas = document.createElement('canvas');
        canvas.width = (options.width || util.width(domNode)) * options.resolution;
        canvas.height = (options.height || util.height(domNode)) * options.resolution;

        if (options.bgcolor) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = options.bgcolor;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
        }

        return canvas;
    }
}

async function makeNodeCopy(node) {
    if (checkTagName(node, 'CANVAS')) {
        return util.makeImage(node.toDataURL());
    }
    return node.cloneNode(false);
}

async function cloneChildrenInOrder(parent, children, filter) {
    for (const child of children) {
        const childClone = await cloneNode(child, filter);
        if (childClone) {
            parent.appendChild(childClone);
        }
    }
    return parent;
}

async function cloneChildren(original, clone, filter) {
    const children = original.childNodes;
    if (children.length === 0) {
        return clone;
    }
    return cloneChildrenInOrder(clone, util.asArray(children), filter);
}

/**
 * 修复文字渐变/图案填充，取色时文本框消失的问题。
 * DOM 版本受限于 Chrome 的限制，无法还原文字渐变和图案填充（background-clip 不支持）
 * 因此降级实现为黑色填充。
 * Canvas 版本，目前无此问题，请愉快食用！
 *
 * See: http://tapd.woa.com/10143861/bugtrace/bugs/view?bug_id=1010143861095000217
 */
function fixTextFill(clone) {
    const { webkitTextFillColor, webkitBackgroundClip, backgroundImage } = clone.style;
    if (
        clone?.tagName === 'SPAN'
        && webkitTextFillColor
        && webkitBackgroundClip
        && webkitBackgroundClip !== 'border-box'
        && backgroundImage
        && backgroundImage !== 'none'
        && browser.isChrome
    ) {
        if (webkitTextFillColor === 'rgba(0, 0, 0, 0)') {
            clone.style.webkitTextFillColor = '';
        }
        clone.style.webkitBackgroundClip = '';
        clone.style.backgroundImage = '';
    }
}

function cloneStyle(node, clone) {
    const source = window.getComputedStyle(node);
    const target = clone.style;
    if (source.cssText) {
        target.cssText = source.cssText;
    } else {
        util.asArray(source).forEach((name) => {
            target.setProperty(
                name,
                source.getPropertyValue(name),
                source.getPropertyPriority(name),
            );
        });
    }
}

function formatCssText(style) {
    const content = style.getPropertyValue('content');
    return `${style.cssText} content: ${content};`;
}

function formatCssProperties(style) {
    return `${util.asArray(style)
        .map(name => `${name}: ${style.getPropertyValue(name)}${style.getPropertyPriority(name) ? ' !important' : ''}`)
        .join('; ')};`;
}

function formatPseudoElementStyle(className, element, style) {
    const selector = `.${className}:${element}`;
    const cssText = style.cssText ? formatCssText(style) : formatCssProperties(style);
    return document.createTextNode(`${selector}{${cssText}}`);
}


function clonePseudoElement(element, node, clone) {
    const style = window.getComputedStyle(node, element);
    const content = style.getPropertyValue('content');

    if (content === '' || content === 'none') return;

    const className = util.uid();
    clone.className = `${clone.className} ${className}`;
    const styleElement = document.createElement('style');
    styleElement.appendChild(formatPseudoElementStyle(className, element, style));
    clone.appendChild(styleElement);
}


function clonePseudoElements(node, clone) {
    [':before', ':after'].forEach((element) => {
        clonePseudoElement(element, node, clone);
    });
}

function copyUserInput(node, clone) {
    if (checkTagName(node, 'TEXTAREA')) clone.innerHTML = node.value;
    if (checkTagName(node, 'INPUT')) clone.setAttribute('value', node.value);
}

function fixSvg(node, clone) {
    if (!(checkTagName(clone, 'svg'))) return;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    if (clone.tagName === 'path' || clone.tagName === 'image') {
        clone.setAttribute('style', null);
        util.asArray(node.style).forEach((name) => {
            clone.style.setProperty(
                name,
                node.style.name,
            );
        });
    }

    const clipPath = node.getAttribute('clip-path');
    if (clipPath) {
        clone.setAttribute('clip-path', clipPath);
        clone.style.clipPath = clipPath;
        clone.style.webkitClipPath = clipPath;
    }

    if (!(checkTagName(clone, 'rect'))) return;
    ['width', 'height'].forEach((attribute) => {
        const value = clone.getAttribute(attribute);
        if (!value) return;

        clone.style.setProperty(attribute, value);
    });
}

function processClone(node, clone) {
    // console.log('node=', node.nodeName, node.tagName, node.__proto__);
    if (!(checkTagName(node))) return clone;
    cloneStyle(node, clone);
    clonePseudoElements(node, clone);
    copyUserInput(node, clone);
    fixSvg(node, clone);
    fixTextFill(clone);
    return clone;
}

async function cloneNode(node, filter, root?) {
    if (!root && filter && !filter(node)) return;
    let clone = await makeNodeCopy(node);
    clone = await cloneChildren(node, clone, filter);
    return processClone(node, clone);
}

function embedFonts(node) {
    return fontFaces.resolveAll()
        .then((cssText) => {
            const styleNode = document.createElement('style');
            node.appendChild(styleNode);
            styleNode.appendChild(document.createTextNode(cssText));
            return node;
        });
}

function inlineImages(node) {
    return images.inlineAll(node)
        .then(() => node);
}

function makeSvgDataUri(node, width, height) {
    return Promise.resolve(node)
        .then((node) => {
            node.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            return new XMLSerializer().serializeToString(node);
        })
        .then(util.escapeXhtml)
        .then(xhtml => `<foreignObject x="0" y="0" width="100%" height="100%">${xhtml}</foreignObject>`)
        .then(foreignObject => `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">${
            foreignObject}</svg>`)
        .then(svg => `data:image/svg+xml;charset=utf-8,${svg}`);
}

function newUtil() {
    return {
        correctDataUriSvg,
        escape,
        parseExtension,
        mimeType,
        dataAsUrl,
        isDataUrl,
        isDataSvgUrl,
        canvasToBlob,
        resolveUrl,
        getAndEncode,
        uid: uid(),
        delay,
        asArray,
        escapeXhtml,
        makeImage,
        width,
        height,
    };

    // dataUri不是base64而是svg的经过foreignObject再加一层datauri会出问题，需要转换为base64或做以下处理
    function correctDataUriSvg(clone) {
        const originDataUri = (clone)?.src;
        let dataUri = originDataUri;
        if (
            typeof dataUri === 'string'
            && dataUri.includes('data:image/svg+xml')
            && !dataUri.includes('data:image/svg+xml;base64')
        ) {
            try {
                // 首先先将svg数据还原成原本的格式
                dataUri = decodeURIComponent(dataUri);
                dataUri = decodeURIComponent(dataUri);
                // 然后将指定字符转换
                dataUri = dataUri.replace(/"/g, '\'')
                    .replace(/%/g, '%25')
                    .replace(/#/g, '%23')
                    .replace(/{/g, '%7B')
                    .replace(/}/g, '%7D')
                    .replace(/</g, '%3C')
                    .replace(/>/g, '%3E')
                    .replace(/\s+/g, ' ');
                clone.src = encodeURI(dataUri);
            } catch (err) {
                clone.src = originDataUri;
            }
        }
    }

    function mimes() {
        /*
             * Only WOFF and EOT mime types for fonts are 'real'
             * see http://www.iana.org/assignments/media-types/media-types.xhtml
             */
        const WOFF = 'application/font-woff';
        const JPEG = 'image/jpeg';

        return {
            woff: WOFF,
            woff2: WOFF,
            ttf: 'application/font-truetype',
            eot: 'application/vnd.ms-fontobject',
            png: 'image/png',
            jpg: JPEG,
            jpeg: JPEG,
            gif: 'image/gif',
            tiff: 'image/tiff',
            svg: 'image/svg+xml',
        };
    }

    function parseExtension(url) {
        // eslint-disable-next-line no-useless-escape
        const match = /\.([^\.\/]*?)$/g.exec(url);
        if (match) return match[1];
        return '';
    }

    function getUrlParam(url, paraName) {
        const arrObj = url.split('?');
        if (arrObj.length > 1) {
            const arrPara = arrObj[1].split('&');
            let arr;

            for (let i = 0; i < arrPara.length; i++) {
                arr = arrPara[i].split('=');
                if (arr != null && arr[0] === paraName) {
                    return arr[1];
                }
            }
            return '';
        }
        return '';
    }

    function mimeType(url) {
        let extension = parseExtension(url).toLowerCase();
        if (extension === '') {
            extension = getUrlParam(url, '_type').toLowerCase();
        }
        return mimes()[extension] || '';
    }

    function isDataUrl(url) {
        return url.search(/^(data:)/) !== -1;
    }

    function isDataSvgUrl(url) {
        return url.search(/^(data:image\/svg\+xml)/) !== -1;
    }

    function toBlob(canvas) {
        return new Promise(((resolve) => {
            const binaryString = window.atob(canvas.toDataURL().split(',')[1]);
            const { length } = binaryString;
            const binaryArray = new Uint8Array(length);

            for (let i = 0; i < length; i++) binaryArray[i] = binaryString.charCodeAt(i);

            resolve(new Blob([binaryArray], {
                type: 'image/png',
            }));
        }));
    }

    function canvasToBlob(canvas) {
        if (canvas.toBlob) {
            return new Promise(((resolve) => {
                canvas.toBlob(resolve);
            }));
        }

        return toBlob(canvas);
    }

    function resolveUrl(url, baseUrl) {
        const doc = document.implementation.createHTMLDocument();
        const base = doc.createElement('base');
        doc.head.appendChild(base);
        const a = doc.createElement('a');
        doc.body.appendChild(a);
        base.href = baseUrl;
        a.href = url;
        return a.href;
    }

    function uid() {
        let index = 0;

        return function () {
            const res = `u${fourRandomChars()}${index}`;
            index += 1;
            return res;

            function fourRandomChars() {
                /* see http://stackoverflow.com/a/6248722/2519373 */
                return (`0000${(Math.random() * Math.pow(36, 4) << 0).toString(36)}`).slice(-4);
            }
        };
    }

    function makeImage(uri) {
        return new Promise(((resolve, reject) => {
            const image = new Image();
            image.onload = function () {
                resolve(image);
            };
            image.onerror = (e) => {
                console.error('[dom-to-image]:', e);
                reject(e);
            };
            image.src = uri;
        }));
    }

    function getAndEncode(url) {
        const TIMEOUT = 30000;
        if (domtoimage.impl.options.cacheBust && !url?.startsWith('blob:')) {
            // Cache bypass so we dont have CORS issues with cached images
            // Source: https://developer.mozilla.org/en/docs/Web/API/XMLHttpRequest/Using_XMLHttpRequest#Bypassing_the_cache
            url += ((/\?/).test(url) ? '&' : '?') + (new Date()).getTime();
        }

        return new Promise(((resolve) => {
            const request = new XMLHttpRequest();

            request.onreadystatechange = done;
            request.ontimeout = timeout;
            request.responseType = 'blob';
            request.timeout = TIMEOUT;
            request.open('GET', url, true);
            request.send();

            let placeholder;
            if (domtoimage.impl.options.imagePlaceholder) {
                const split = domtoimage.impl.options.imagePlaceholder.split(/,/);
                if (split?.[1]) {
                    placeholder = split[1];
                }
            }

            function done() {
                if (request.readyState !== 4) return;

                if (request.status !== 200) {
                    if (placeholder) {
                        resolve(placeholder);
                    } else {
                        fail(`cannot fetch resource: ${url}, status: ${request.status}`);
                    }

                    return;
                }

                const encoder = new FileReader();
                encoder.onloadend = function () {
                    const content = (encoder.result as string).split(/,/)[1];
                    resolve(content);
                };
                encoder.readAsDataURL(request.response);
            }

            function timeout() {
                if (placeholder) {
                    resolve(placeholder);
                } else {
                    fail(`timeout of ${TIMEOUT}ms occured while fetching resource: ${url}`);
                }
            }

            function fail(message) {
                console.error(message);
                resolve('');
            }
        }));
    }

    function dataAsUrl(content, type) {
        return `data:${type};base64,${content}`;
    }

    function escape(string) {
        // eslint-disable-next-line no-useless-escape
        return string.replace(/([.*+?^${}()|\[\]\/\\])/g, '\\$1');
    }

    function delay(ms) {
        return function (arg) {
            return new Promise(((resolve) => {
                setTimeout(() => {
                    resolve(arg);
                }, ms);
            }));
        };
    }

    function asArray(arrayLike) {
        const array: any[] = [];
        const { length } = arrayLike;
        for (let i = 0; i < length; i++) array.push(arrayLike[i]);
        return array;
    }

    function escapeXhtml(string) {
        // 转义文本中的%/#
        return string.replace(/%(\d+)/g, '%25$1')
            .replace(/#/g, '%23')
            .replace(/\n/g, '%0A');
    }

    function width(node) {
        const leftBorder = px(node, 'border-left-width');
        const rightBorder = px(node, 'border-right-width');
        return node.scrollWidth + leftBorder + rightBorder;
    }

    function height(node) {
        const topBorder = px(node, 'border-top-width');
        const bottomBorder = px(node, 'border-bottom-width');
        return node.scrollHeight + topBorder + bottomBorder;
    }

    function px(node, styleProperty) {
        const value = window.getComputedStyle(node).getPropertyValue(styleProperty);
        return parseFloat(value.replace('px', ''));
    }
}

function newInliner() {
    const URL_REGEX = /url\(['"]?([^'"]+?)['"]?\)/g;

    return {
        inlineAll,
        shouldProcess,
        impl: {
            readUrls,
            inline,
        },
    };

    function shouldProcess(string) {
        return string.search(URL_REGEX) !== -1;
    }

    function readUrls(string) {
        const result: any[] = [];
        let match;

        while ((match = URL_REGEX.exec(string)) !== null) {
            result.push(match[1]);
        }
        return result.filter(url => !util.isDataUrl(url));
    }

    function inline(string, url, baseUrl, get) {
        return Promise.resolve(url)
            .then(url => (baseUrl ? util.resolveUrl(url, baseUrl) : url))
            .then(get || util.getAndEncode)
            .then(data => util.dataAsUrl(data, util.mimeType(url)))
            .then(dataUrl => string.replace(urlAsRegex(url), `$1${dataUrl}$3`));

        function urlAsRegex(url) {
            return new RegExp(`(url\\(['"]?)(${util.escape(url)})(['"]?\\))`, 'g');
        }
    }

    function inlineAll(string, baseUrl?, get?) {
        if (nothingToInline()) return Promise.resolve(string);

        return Promise.resolve(string)
            .then(readUrls)
            .then((urls) => {
                let done = Promise.resolve(string);
                urls.forEach((url) => {
                    done = done.then(string => inline(string, url, baseUrl, get));
                });
                return done;
            });

        function nothingToInline() {
            return !shouldProcess(string);
        }
    }
}

function newFontFaces() {
    return {
        resolveAll,
        impl: {
            readAll,
        },
    };

    function resolveAll() {
        return readAll()
            .then(webFonts => Promise.all(webFonts.map(webFont => webFont.resolve())))
            .then(cssStrings => cssStrings.join('\n'));
    }

    function readAll() {
        return Promise.resolve(util.asArray(document.styleSheets))
            .then(getCssRules)
            .then(selectWebFontRules)
            .then(rules => rules.map(newWebFont));

        function selectWebFontRules(cssRules) {
            return cssRules
                .filter(rule => rule.type === CSSRule.FONT_FACE_RULE)
                .filter((rule) => {
                    console.log(rule.style.getPropertyValue('href'));
                    return inliner.shouldProcess(rule.style.getPropertyValue('href'));
                });
        }

        function getCssRules(styleSheets) {
            const cssRules = [];
            styleSheets.forEach((sheet) => {
                try {
                    util.asArray(sheet.cssRules || []).forEach(cssRules.push.bind(cssRules));
                } catch (e) {
                    console.log(`Error while reading CSS rules from ${sheet.href}`, e.toString());
                }
            });
            return cssRules;
        }

        function newWebFont(webFontRule) {
            return {
                resolve: function resolve() {
                    const baseUrl = webFontRule.parentStyleSheet?.href;
                    return inliner.inlineAll(webFontRule.cssText, baseUrl);
                },
                src() {
                    return webFontRule.style.getPropertyValue('src');
                },
            };
        }
    }
}

function newImages() {
    return {
        inlineAll,
        impl: {
            newImage,
        },
    };

    function newImage(element) {
        return {
            inline,
        };

        function inline(get?) {
            let myStr;
            if (element.src == undefined) {
                myStr = element.href.animVal;
            } else {
                myStr = element.src;
            }
            // // 如果img src是svg编码，需要转换，否则转img标签转出的svg会报错
            if (myStr.includes('.svg') || util.isDataSvgUrl(myStr)) {
                return svgToBase64ByCanvas(myStr).then((url) => {
                    element.src = url;
                    element.setAttribute('href', url);
                });
            }
            if (util.isDataUrl(myStr)) {
                return;
            }

            return Promise.resolve(myStr)
                .then(get || util.getAndEncode)
                .then(data => util.dataAsUrl(data, util.mimeType(myStr)))
                .then(dataUrl => new Promise(((resolve, reject) => {
                    element.onerror = reject;
                    element.src = dataUrl;
                    element.setAttribute('href', dataUrl);
                    resolve(undefined);
                })));
        }
    }

    function inlineAll(node) {
        if (!(checkTagName(node))) return Promise.resolve(node);
        return inlineBackground(node)
            .then(() => {
                if (checkTagName(node, 'IMG') || checkTagName(node, 'image')) return newImage(node).inline();
                return Promise.all(util.asArray(node.childNodes).map(child => inlineAll(child)));
            });

        function inlineBackground(node) {
            const background = node.style.getPropertyValue('background');

            if (!background) return Promise.resolve(node);

            return inliner.inlineAll(background)
                .then((inlined) => {
                    node.style.setProperty(
                        'background',
                        inlined,
                        node.style.getPropertyPriority('background'),
                    );
                })
                .then(() => node);
        }
    }
}
// })(this);
export default domtoimage;

export const testObj = {
    cloneNode,
    cloneChildren,
    fixTextFill,
    formatCssText,
    formatCssProperties,
    formatPseudoElementStyle,
    copyUserInput,
};
